import {action, computed} from '@api'
import {AssortmentCardStore, BuilderCardStore} from '@modules/catalog'
import {CatalogDataItemUIStore} from '@modules/catalog/sidebar/CatalogItemsList'
import {ArrowShape, CatalogItemCardShape, createDefaultStyle, EllipseShape, FrameShape, FrameShapeStore, FRAME_BOUNDS_PADDING, FRAME_TITLE_HEIGHT, ImageShape, RectangleShape, TDShape, TDShapeType} from '@modules/whiteboard/shapes'
import {WhiteboardStore} from '@modules/whiteboard/WhiteboardStore'
import {CardImageSourceSettings, CatalogStore, FileImage} from '@stores'
import {Utils} from '@tldraw/core'
import Vec from '@tldraw/vec'
import {guid} from '@utils'
import {toJS} from 'mobx'

export const CARD_CREATE_SIZE_SCALING_FACTOR = 2.5

export class WhiteboardCreateShapesStore {
  constructor(public store: WhiteboardStore) {}

  get page() { return this.store.page }

  @computed get pagePointFromContextMenu() {
    const {store: {getPagePoint, contextMenu: {mousePosition}}} = this

    const r = mousePosition ? getPagePoint([mousePosition.clientX, mousePosition.clientY]) : null

    //console.debug(`Context menu -> virtual canvas`, {mousePosition, r})
    return r
  }

  @action createRectangle = () => {
    const id = guid(), {page, pagePointFromContextMenu, store, store: {camera, ui, nextShapeIndex}} = this

    const rect = {
      id,
      type: 'rectangle',
      parentId: page.id,
      name: 'Rectangle',
      childIndex: nextShapeIndex,
      rotation: 0,
      point: pagePointFromContextMenu ?? [300 * nextShapeIndex, 0],
      size: [250, 250],
      style: ui.currentStyle
    } as RectangleShape

    this.createShapeOnCurrentPage(rect)
  }

  @action createEllipse = () => {
    const id = guid(), {createShapeOnCurrentPage, store: {nextShapeIndex}} = this
    const r = {
      id,
      type: 'ellipse',
      name: 'Circle',
      rotation: 0,
      radius: [50, 50],
      point: [50 * nextShapeIndex, 50 * nextShapeIndex]
    } as EllipseShape

    createShapeOnCurrentPage(r)
  }

  @action createLine = () => {
    const id = guid(), {page, createShapeOnCurrentPage, store: {ui, nextShapeIndex}} = this
    const r = {
      id,
      type: 'line',
      parentId: page.id,
      name: 'Line',
      childIndex: nextShapeIndex,
      rotation: 0,
      point: [50 * nextShapeIndex, 50],
      bend: 1,
      decorations: null
    } as any as ArrowShape

    createShapeOnCurrentPage(r)
  }

  @action private createShapeOnCurrentPage = (shape: TDShape) => {
    const {id} = shape, {store, store: {camera, ui, page, page: {shapes}, myPageState, nextShapeIndex}} = this
    shapes[id] = {
      style: createDefaultStyle(),
      parentId: page.id,
      childIndex: nextShapeIndex,
      ...shape
    }

    myPageState.selectedIds = [id]

    // console.debug(`Added shape`, toJS(shapes[id]))
    if (!store.isSelectionWithinViewport) {
      camera.zoomToSelection()
    }
  }

  /**
   * Don't put in action - we want to avoid freezing the UI as we create many cards
   * @param catalogItems
   * @param startingPoint
   */
  createBuilderCards = (catalogItems: Array<BuilderCardStore | CatalogDataItemUIStore>, startingPoint?: number[], offsetStartingPointByHalfWidthAndHeight = false) => {
    const {store, store: {tab, camera, myPageState, myPageState: {selectedIds}, page, page: {shapes}, getShapeBounds, shapesInOrder}, store: tldraw} = this

    console.log(`Sending catalog items to whiteboard`, {catalogItems, page})

    let ignoreAfter = tab.ignoreSettingUpdates
    tab.ignoreSettingUpdates = true

    try {
      const INTRA_CARD_SPACING = 8
      myPageState.selectedIds = []

      let repositionCreatedShapes = true

      const hadStartingPoint = startingPoint != null
      if (startingPoint) {
        repositionCreatedShapes = false
      } else {
        startingPoint = camera.viewportCenterPoint
        // After creating items in the center of the viewport, reposition them -width/2 so they are dead centered
        const shapeToStartFrom = _.last(store.selectedShapes) ?? _.last(shapesInOrder)
        if (shapeToStartFrom) {
          const b = getShapeBounds(shapeToStartFrom)

          console.log(b, shapeToStartFrom)
          if (b) {
            startingPoint = [b.maxX, b.minY]
            repositionCreatedShapes = false
          }
        }
      }

      let point = [...startingPoint]
      tldraw.selectTool('select')

      const createCatalogItemCardShape = ({itemId, imageId, size, workspaceId, visibleFields, imageSource}: { imageSource?: CardImageSourceSettings, itemId, imageId?, size, workspaceId, visibleFields? }) => {
        const id = guid()
        selectedIds.push(id)

        // console.log(toJS({offsetStartingPointByHalfWidthAndHeight, hadStartingPoint, point, startingPoint}))

        if (offsetStartingPointByHalfWidthAndHeight && hadStartingPoint && _.isEqual(point, startingPoint)) {
          console.log(`before`, point)
          point = Vec.sub(point, [size[0] / 2, size[1] / 2])
          console.log(`after`, point)
        }

        const card = {
          id,
          type: 'catalogItem',
          parentId: page.id,
          childIndex: tldraw.nextShapeIndex,
          rotation: 0,
          point: [...point],
          size,
          isGenerated: true,
          style: createDefaultStyle(),
          cardData: {
            itemId,
            imageId,
            workspace: workspaceId,
            view: 'builderCard',
            visibleFields: visibleFields ?? []
          }
        } as CatalogItemCardShape

        // console.debug(`Created builder card shape: `, {card, workspaceId, itemId})

        if (imageSource?.view === 'stack') {
          // console.log(imageSource.stackedCards.spread)
          point = Vec.add(point, [size[0] * imageSource.stackedCards.spread, 0])
        } else {
          point = Vec.add(point, [INTRA_CARD_SPACING + size[0], 0])
        }
        shapes[id] = card

        return shapes[id] as CatalogItemCardShape
      }

      const finalShapes = catalogItems.flatMap((store, i) => {
        const {catalog: {isShowingMainCatalogItemAsImageSource, settings: {imageSource}}, id: itemId, workspace, workspaces: {byIndexScalar}, visibleCardFields, prototypicalCardSize} = store

        if (store instanceof CatalogDataItemUIStore) {
          const size = [prototypicalCardSize.width, prototypicalCardSize.height],
            id = guid()
          selectedIds.push(id)

          if (offsetStartingPointByHalfWidthAndHeight && hadStartingPoint && _.isEqual(point, startingPoint)) {
            point = Vec.sub(point, [size[0] / 2, size[1] / 2])
          }

          const card = {
            id,
            type: 'catalogItem',
            parentId: page.id,
            childIndex: tldraw.nextShapeIndex,
            rotation: 0,
            point: [...point],
            size,
            style: createDefaultStyle(),
            cardData: {
              itemId,
              workspace: workspace._id,
              view: 'listPreviewCard'
            }
          } as CatalogItemCardShape
          shapes[id] = card
        } else {
          const {imagesToRender, selectedImage} = store
          const size = selectedImage?.sizeArr ?? [prototypicalCardSize.width * CARD_CREATE_SIZE_SCALING_FACTOR, prototypicalCardSize.height * CARD_CREATE_SIZE_SCALING_FACTOR]

          const r = [
            isShowingMainCatalogItemAsImageSource && createCatalogItemCardShape({itemId, size, visibleFields: visibleCardFields.map(f => f.field), workspaceId: workspace._id}),
            ...(imageSource.view === 'stack' ? imagesToRender : !isShowingMainCatalogItemAsImageSource ? [_.first(imagesToRender)] : []).filter(_.identity)
              .map(imageStore => {
                const {path, image: {exId}} = imageStore,
                  pathItem = _.last(path)

                return createCatalogItemCardShape({
                  imageSource,
                  itemId: pathItem.where[0].value,
                  imageId:
                  exId,
                  workspaceId: byIndexScalar[pathItem.esIndex]._id,
                  size: imageStore.sizeArr ?? size
                })
              })
          ].filter(_.identity)

          // So the cards stack left to right instead of vice versa
          r.map(s => s.childIndex).reverse().forEach((index, i) => r[i].childIndex = index)

          if (imageSource.view === 'stack') {
            point = Vec.add(point, [_.sumBy(r, v => v.size[0]) * (1 - imageSource.stackedCards.spread) + 2 * INTRA_CARD_SPACING, 0])
          }

          return r
        }
      }).filter(_.identity)

      // Move the shapes so that their center is at the original point we specified
      if (repositionCreatedShapes) {
        const {selectedBounds, translateShapes} = tldraw
        translateShapes(tldraw.selectedShapes, [-.5 * selectedBounds.width, -.5 * selectedBounds.height])
      }

      tldraw.selectedShapes.forEach(s => s.isGenerated = false)

      tldraw.align('centerVertical')
      // store.group()
      camera.zoomToSelection(camera.zoom)

      setTimeout(action(() => {
        const {shapesByType: {frame}, selectedShapes, shapeUtil: {getBounds}, selectedIdsMap, shapeComponentStores} = tldraw
        const f = (frame ?? []).find(f => (shapeComponentStores[f.id] as FrameShapeStore)?.shapesContainedBySet?.has(finalShapes[0].id)) as FrameShape

        if (f) {
          // Increase the bounds of the frame to be greater than that of the new items
          const store = shapeComponentStores[f.id] as FrameShapeStore,
            frameBounds = getBounds(f)

          if (store.shapesContainedBy.every(s => selectedIdsMap.has(s.id))) {
            // move all the shapes to align in the upper left of the frame first
            const {selectedBounds} = tldraw,
              delta = [
                frameBounds.minX - selectedBounds.minX + FRAME_BOUNDS_PADDING,
                frameBounds.minY - selectedBounds.minY + FRAME_TITLE_HEIGHT + FRAME_BOUNDS_PADDING
              ]

            tldraw.translateShapes(selectedShapes, delta)
          }

          const {selectedBounds} = tldraw
          if (!Utils.boundsContain(frameBounds, selectedBounds)) {
            if (f.point[0] > selectedBounds.minX) {
              f.point[0] = selectedBounds.minX - FRAME_BOUNDS_PADDING
            }
            if (f.point[1] > selectedBounds.minY) {
              f.point[1] = selectedBounds.minY - FRAME_BOUNDS_PADDING - FRAME_TITLE_HEIGHT
            }

            if (frameBounds.maxX < selectedBounds.maxX) {
              f.size[0] = selectedBounds.minX - f.point[0] + selectedBounds.width + FRAME_BOUNDS_PADDING
            }
            if (frameBounds.maxY < selectedBounds.maxY) {
              f.size[1] = selectedBounds.minY - f.point[1] + selectedBounds.height + FRAME_BOUNDS_PADDING
            }
          }
        }
      }), 50)
      return finalShapes
    } finally {
      tab.ignoreSettingUpdates = ignoreAfter
    }
  }

  @action createImageShape = async (image: FileImage) => {
    const {store: {currentPageId: parentId, nextShapeIndex, mouseLocationVirtual, page: {shapes}, myPageState: {selectedIds}, camera, catalog: {img: {uploadImagesToC8}}}} = this

    const [{exId}] = await uploadImagesToC8([image]),
      img = new Image()

    img.src = image.image ?? URL.createObjectURL(image.file)

    img.onload = () => {
      const imageShape: ImageShape = {
        id: guid(),
        parentId,
        childIndex: nextShapeIndex,
        point: mouseLocationVirtual ?? camera.rendererCenterPoint,
        size: [img.naturalWidth, img.naturalHeight],
        type: 'image',
        name: image.file.name,
        rotation: 0,
        style: createDefaultStyle(),
        exId
      }

      console.log(`Create image shape`, imageShape)
      shapes[imageShape.id] = imageShape
      selectedIds.push(imageShape.id)
    }
  }

  @action createAssortmentCard = (card: AssortmentCardStore) => {

  }
}



// WEBPACK FOOTER //
// ./src/modules/whiteboard/WhiteboardCreateShapesStore.ts